import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import TopBar from '../Dashboard/TopBar';
import axios from 'axios';
import { 
  Plus, Minus, ArrowLeft, ShoppingBag, X, Trash2, 
  Utensils, Hash, User, Phone, Search, ChevronRight, ChevronLeft, Image as ImageIcon 
} from 'lucide-react';

const Order = ({ isModal = false, onOrderSuccess }) => {
  const navigate = useNavigate();
  
  // Responsive States
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  // UI States
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  
  // Data States
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Cart & Form States
  const [cart, setCart] = useState([]);
  const [formData, setFormData] = useState({ name: '', mobile: '', table: '' });
  
  const brandColor = "#ae7d54";

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    fetchInitialData();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [menuRes, tableRes, catRes] = await Promise.all([
        axios.get('https://admin.theporch.in/api/menu_items'),
        axios.post('https://admin.theporch.in/api/tables/fetch'),
        axios.get('https://admin.theporch.in/api/menu_categories')
      ]);
      setMenuItems(menuRes.data);
      setTables(tableRes.data);
      setCategories(['All', ...catRes.data]);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    return menuItems.filter(item => {
      const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [menuItems, selectedCategory, searchQuery]);

  const addToCart = (item) => {
    const exists = cart.find(x => x.id === item.id);
    if (exists) {
      setCart(cart.map(x => x.id === item.id ? { ...x, qty: x.qty + 1 } : x));
    } else {
      setCart([...cart, { id: item.id, display_name: item.title, display_price: item.price, qty: 1 }]);
    }
    if (isDesktop && !isCartOpen) setIsCartOpen(true);
  };

  const updateQty = (id, change) => {
    setCart(prevCart => {
      return prevCart.map(item => {
        if (item.id === id) {
          const newQty = item.qty + change;
          // If new quantity is 0 or less, we'll filter it out in the next step
          return { ...item, qty: newQty };
        }
        return item;
      }).filter(item => item.qty > 0); // Automatically removes item if qty drops to 0
    });
  };

  const removeFromCart = (id) => setCart(cart.filter(item => item.id !== id));
  const total = cart.reduce((acc, curr) => acc + (curr.display_price * curr.qty), 0);

  const submitOrder = async () => {
    if (!formData.table || cart.length === 0) return alert("Select Table and add items!");
    const payload = {
      table_number: formData.table,
      customer_name: formData.name || "Guest",
      customer_mobile: formData.mobile || "",
      items: cart.map(i => `${i.display_name} (x${i.qty})`).join(', '),
      total_amount: total
    };
    try {
      await axios.post('https://admin.theporch.in/api/orders', payload);
      alert("Order Successful!");
      isModal ? onOrderSuccess() : navigate('/tables/live-orders');
    } catch (err) { alert("Error saving order."); }
  };

  const getGridTemplate = () => {
    if (isMobile) return 'repeat(auto-fill, minmax(140px, 1fr))';
    if (isTablet) return 'repeat(auto-fill, minmax(180px, 1fr))';
    return isCartOpen ? 'repeat(auto-fill, minmax(200px, 1fr))' : 'repeat(auto-fill, minmax(220px, 1fr))';
  };

  const getItemInCart = (itemId) => cart.find(item => item.id === itemId);

  return (
    <div style={styles.pageWrapper}>
      <style>
        {`
          .custom-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
          .custom-scroll::-webkit-scrollbar-track { background: transparent; }
          .custom-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
          .category-nav::-webkit-scrollbar { display: none; }
          .category-nav { -ms-overflow-style: none; scrollbar-width: none; }
          
          /* Dynamic Card Image Height */
          .food-image-container { 
            height: ${isMobile ? '100px' : '150px'}; 
          }
        `}
      </style>

      {!isModal && isDesktop && <Sidebar isOpen={isSidebarOpen} />}

      <div style={styles.mainContentArea}>
        <section style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
          {!isModal && <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />}
          
          <div className="custom-scroll" style={{ padding: isMobile ? '12px' : '24px', flex: 1, overflowY: 'auto' }}>
            
            <div style={{ ...styles.headerContainer, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }}>
               <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {!isModal && <button onClick={() => navigate(-1)} style={styles.backBtnStyle}><ArrowLeft size={18} /></button>}
                    <h1 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '800', margin: 0, color: '#1e293b' }}>Porch Cafe</h1>
                  </div>
                  
                  {isMobile && (
                    <div style={styles.cartTrigger} onClick={() => setIsCartOpen(true)}>
                       <ShoppingBag size={22} color={brandColor} />
                       {cart.length > 0 && <span style={styles.badgeStyle}>{cart.length}</span>}
                    </div>
                  )}
               </div>

               <div style={{ ...styles.searchWrapper, maxWidth: isMobile ? '100%' : '350px' }}>
                  <Search size={18} style={styles.searchIcon} />
                  <input 
                    style={styles.searchInput} 
                    placeholder="Search dishes..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
               </div>

               {!isMobile && (
                 <div style={styles.cartTrigger} onClick={() => setIsCartOpen(!isCartOpen)}>
                    {isCartOpen ? <ChevronRight size={20} color={brandColor} /> : <ChevronLeft size={20} color={brandColor} />}
                    <ShoppingBag size={22} color={brandColor} />
                    {cart.length > 0 && !isCartOpen && <span style={styles.badgeStyle}>{cart.length}</span>}
                 </div>
               )}
            </div>

            <div className="category-nav" style={styles.categoryNav}>
              {categories.map(cat => (
                <button 
                  key={cat} 
                  onClick={() => setSelectedCategory(cat)}
                  style={selectedCategory === cat ? styles.activeCatBtn : styles.inactiveCatBtn}
                >
                  {cat}
                </button>
              ))}
            </div>

            {loading ? (
              <div style={styles.centerMsg}><Utensils className="animate-spin" /> Loading Menu...</div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: getGridTemplate(), 
                gap: isMobile ? '12px' : '20px',
                paddingBottom: '40px'
              }}>
                {filteredItems.map(item => {
                  const cartItem = getItemInCart(item.id);
                  const isInCart = !!cartItem;

                  return (
                    <div 
                      key={item.id} 
                      style={{
                        ...styles.foodCard,
                        borderColor: isInCart ? brandColor : '#e2e8f0',
                        backgroundColor: isInCart ? '#fefaf6' : 'white',
                        transform: isInCart ? 'scale(0.98)' : 'scale(1)',
                        maxWidth: isMobile ? '100%' : '280px'
                      }}
                    >
                      <div className="food-image-container" style={styles.imageContainer}>
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.title} style={styles.foodImage} />
                        ) : (
                          <ImageIcon size={30} color="#cbd5e1" />
                        )}
                      </div>
                      <div style={{ padding: isMobile ? '10px' : '15px' }}>
                        <div style={styles.foodTitle}>{item.title}</div>
                        <div style={styles.foodCategory}>{item.category}</div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                          <span style={styles.foodPrice}>₹{item.price}</span>
                          
                          {isInCart ? (
                            <div style={styles.cardQtyToggle}>
                              <button onClick={() => updateQty(item.id, -1)} style={styles.cardQtyBtn}><Minus size={12} /></button>
                              <span style={styles.cardQtyValue}>{cartItem.qty}</span>
                              <button onClick={() => updateQty(item.id, 1)} style={styles.cardQtyBtn}><Plus size={12} /></button>
                            </div>
                          ) : (
                            <button onClick={() => addToCart(item)} style={styles.addIconCircle}>
                              <Plus size={14} color="white" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <aside style={{ 
          ...styles.cartAside,
          width: isCartOpen ? (isMobile ? '100%' : '380px') : '0px', 
          position: isMobile ? 'fixed' : 'relative',
          opacity: isCartOpen ? 1 : 0,
          visibility: isCartOpen ? 'visible' : 'hidden',
          zIndex: 1000,
          boxShadow: isCartOpen ? '-5px 0 25px rgba(0,0,0,0.05)' : 'none'
        }}>
          <div style={{ ...styles.cartContent, width: isMobile ? '100vw' : '380px' }}>
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b' }}>Checkout</h2>
              <button onClick={() => setIsCartOpen(false)} style={styles.closeCartBtn}><X size={20} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              <div style={styles.inputContainer}>
                 <Hash size={16} style={styles.inputIcon} />
                 <select style={styles.inputWithIcon} value={formData.table} onChange={e => setFormData({ ...formData, table: e.target.value })}>
                   <option value="">Select Table</option>
                   {tables.map(t => <option key={t.id} value={t.table_number}>Table {t.table_number}</option>)}
                 </select>
              </div>
              <div style={styles.inputContainer}>
                 <User size={16} style={styles.inputIcon} />
                 <input style={styles.inputWithIcon} placeholder="Guest Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div style={styles.inputContainer}>
                 <Phone size={16} style={styles.inputIcon} />
                 <input style={styles.inputWithIcon} placeholder="Mobile No." value={formData.mobile} onChange={e => setFormData({ ...formData, mobile: e.target.value })} />
              </div>
            </div>

            <div className="custom-scroll" style={{ flex: 1, overflowY: 'auto', paddingRight: '5px' }}>
              {cart.length === 0 ? (
                <div style={styles.emptyCartStyle}><Utensils size={30} style={{ opacity: 0.2 }} /><p>Your cart is empty</p></div>
              ) : cart.map(item => (
                <div key={item.id} style={styles.cartItemRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: '#334155' }}>{item.display_name}</div>
                    <div style={{ color: brandColor, fontWeight: '700', fontSize: '14px' }}>₹{item.display_price * item.qty}</div>
                  </div>
                  <div style={styles.qtyGroup}>
                    <button onClick={() => updateQty(item.id, -1)} style={styles.qtyBtn}><Minus size={12} /></button>
                    <span style={{ fontWeight: '800', width: '20px', textAlign: 'center', fontSize: '13px' }}>{item.qty}</span>
                    <button onClick={() => updateQty(item.id, 1)} style={styles.qtyBtn}><Plus size={12} /></button>
                    <button onClick={() => removeFromCart(item.id)} style={styles.delBtn}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>

            <div style={styles.cartFooter}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                <span style={{ fontWeight: '600', color: '#64748b' }}>Grand Total</span>
                <span style={{ fontSize: '22px', fontWeight: '900', color: '#1e293b' }}>₹{total}</span>
              </div>
              <button onClick={submitOrder} style={styles.checkoutBtn}>Confirm Order</button>
            </div>
          </div>
        </aside>

        {isMobile && isCartOpen && <div style={styles.backdrop} onClick={() => setIsCartOpen(false)} />}
      </div>
    </div>
  );
};

const styles = {
  pageWrapper: { display: 'flex', height: '100%', width: '100%', background: '#f8fafc', overflow: 'hidden' },
  mainContentArea: { flex: 1, display: 'flex', height: '100%', width: '100%', overflow: 'hidden', position: 'relative' },
  headerContainer: { marginBottom: '20px', display: 'flex', gap: '15px' },
  searchWrapper: { position: 'relative', flex: 1 },
  searchInput: { width: '100%', padding: '12px 15px 12px 45px', borderRadius: '12px', border: '1px solid #e2e8f0', outline: 'none', background: 'white', boxSizing: 'border-box', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' },
  searchIcon: { position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
  cartTrigger: { position: 'relative', cursor: 'pointer', background: 'white', padding: '10px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '4px' },
  badgeStyle: { position: 'absolute', top: '-5px', right: '-5px', background: '#ef4444', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '50%', fontWeight: 'bold' },
  categoryNav: { display: 'flex', gap: '8px', marginBottom: '20px', overflowX: 'auto', paddingBottom: '8px', WebkitOverflowScrolling: 'touch' },
  inactiveCatBtn: { padding: '8px 18px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '14px', color: '#64748b', transition: 'all 0.2s' },
  activeCatBtn: { padding: '8px 18px', borderRadius: '10px', border: 'none', background: '#ae7d54', color: 'white', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '14px', boxShadow: '0 4px 12px rgba(174, 125, 84, 0.25)' },
  foodCard: { background: 'white', borderRadius: '20px', border: '2px solid transparent', overflow: 'hidden', transition: 'all 0.3s ease', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01)', display: 'flex', flexDirection: 'column' },
  imageContainer: { background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  foodImage: { width: '100%', height: '100%', objectFit: 'cover' },
  foodTitle: { fontWeight: '700', fontSize: '15px', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  foodCategory: { fontSize: '12px', color: '#94a3b8', marginTop: '2px' },
  foodPrice: { color: '#ae7d54', fontWeight: '800', fontSize: '16px' },
  addIconCircle: { background: '#ae7d54', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', boxShadow: '0 4px 8px rgba(174, 125, 84, 0.3)' },
  cardQtyToggle: { display: 'flex', alignItems: 'center', gap: '8px', background: '#ae7d54', padding: '4px 8px', borderRadius: '20px' },
  cardQtyBtn: { background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  cardQtyValue: { color: 'white', fontWeight: 'bold', fontSize: '13px', minWidth: '15px', textAlign: 'center' },
  cartAside: { background: 'white', height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', right: 0, top: 0 },
  cartContent: { padding: '24px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' },
  inputContainer: { position: 'relative' },
  inputIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
  inputWithIcon: { width: '100%', padding: '12px 10px 12px 40px', borderRadius: '12px', border: '1px solid #f1f5f9', background: '#f8fafc', boxSizing: 'border-box', fontSize: '14px', outline: 'none' },
  cartItemRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f8fafc' },
  qtyGroup: { display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', padding: '6px', borderRadius: '10px' },
  qtyBtn: { background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' },
  delBtn: { color: '#f87171', marginLeft: '6px', background: 'none', border: 'none', cursor: 'pointer' },
  cartFooter: { borderTop: '2px dashed #f1f5f9', paddingTop: '20px', marginTop: 'auto' },
  checkoutBtn: { width: '100%', padding: '16px', borderRadius: '14px', border: 'none', background: '#1e293b', color: 'white', fontWeight: '800', cursor: 'pointer', fontSize: '16px', transition: 'transform 0.2s', boxShadow: '0 10px 15px -3px rgba(30, 41, 59, 0.2)' },
  backdrop: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(15, 23, 42, 0.5)', zIndex: 999, backdropFilter: 'blur(4px)' },
  centerMsg: { textAlign: 'center', padding: '40px', color: '#94a3b8', width: '100%' },
  backBtnStyle: { border: 'none', background: 'white', padding: '10px', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  emptyCartStyle: { textAlign: 'center', marginTop: '60px', color: '#cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' },
  closeCartBtn: { background: '#f1f5f9', border: 'none', padding: '8px', borderRadius: '10px', cursor: 'pointer', color: '#64748b' }
};

export default Order;